import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import dayjs from 'dayjs';
import moment from 'moment';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/pt-br';
import 'moment/locale/es';
import 'moment/locale/pt-br';
import { translations } from './translations';

export type LanguageCode = keyof typeof translations;

interface TranslationValues {
  [key: string]: string | number;
}

interface LanguageOption {
  code: LanguageCode;
  nativeLabel: string;
}

interface I18nContextData {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string, values?: TranslationValues) => string;
  languages: LanguageOption[];
}

const LANGUAGE_STORAGE_KEY = '@Sigeclin:language';
const DEFAULT_LANGUAGE: LanguageCode = 'pt-BR';

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'pt-BR', nativeLabel: 'Português (Brasil)' },
  { code: 'en-US', nativeLabel: 'English (US)' },
  { code: 'es-ES', nativeLabel: 'Español' },
];

const DAYJS_LOCALE_BY_LANGUAGE: Record<LanguageCode, string> = {
  'pt-BR': 'pt-br',
  'en-US': 'en',
  'es-ES': 'es',
};

const SUPPORTED_LANGUAGES = new Set<LanguageCode>(['pt-BR', 'en-US', 'es-ES']);

const getWindowLanguage = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  return (
    window.localStorage.getItem(LANGUAGE_STORAGE_KEY) || navigator.language
  );
};

export const normalizeLanguage = (language?: string | null): LanguageCode => {
  if (!language) {
    return DEFAULT_LANGUAGE;
  }

  if (SUPPORTED_LANGUAGES.has(language as LanguageCode)) {
    return language as LanguageCode;
  }

  const normalizedPrefix = language.toLowerCase();

  if (normalizedPrefix.startsWith('en')) {
    return 'en-US';
  }

  if (normalizedPrefix.startsWith('es')) {
    return 'es-ES';
  }

  return 'pt-BR';
};

const detectInitialLanguage = (): LanguageCode => {
  return normalizeLanguage(getWindowLanguage());
};

let currentLanguage: LanguageCode = detectInitialLanguage();

const getNestedValue = (target: unknown, path: string): unknown => {
  return path.split('.').reduce<unknown>((result, segment) => {
    if (!result || typeof result !== 'object') {
      return undefined;
    }

    return (result as Record<string, unknown>)[segment];
  }, target);
};

const interpolate = (template: string, values?: TranslationValues) => {
  if (!values) {
    return template;
  }

  return template.replace(/\{\{(.*?)\}\}/g, (_, rawKey: string) => {
    const key = rawKey.trim();
    const value = values[key];
    return value === undefined ? '' : String(value);
  });
};

export const getCurrentLanguage = (): LanguageCode => currentLanguage;

export const preserveLanguagePreference = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  return window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
};

export const restoreLanguagePreference = (language: string | null) => {
  if (typeof window === 'undefined' || !language) {
    return;
  }

  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
};

export const clearStoragePreservingLanguage = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const savedLanguage = preserveLanguagePreference();
  window.sessionStorage.clear();
  window.localStorage.clear();
  restoreLanguagePreference(savedLanguage);
};

export const translate = (
  key: string,
  values?: TranslationValues,
  language: LanguageCode = currentLanguage,
): string => {
  const primaryMatch = getNestedValue(translations[language], key);
  const fallbackMatch = getNestedValue(translations[DEFAULT_LANGUAGE], key);
  const rawValue =
    typeof primaryMatch === 'string'
      ? primaryMatch
      : typeof fallbackMatch === 'string'
        ? fallbackMatch
        : key;

  return interpolate(rawValue, values);
};

const I18nContext = createContext<I18nContextData>({} as I18nContextData);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<LanguageCode>(currentLanguage);

  const setLanguage = useCallback((nextLanguage: LanguageCode) => {
    setLanguageState(normalizeLanguage(nextLanguage));
  }, []);

  useEffect(() => {
    const normalizedLanguage = normalizeLanguage(language);
    currentLanguage = normalizedLanguage;

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
      document.documentElement.lang = normalizedLanguage;
    }

    const locale = DAYJS_LOCALE_BY_LANGUAGE[normalizedLanguage];
    dayjs.locale(locale);
    moment.locale(locale);
  }, [language]);

  const t = useCallback(
    (key: string, values?: TranslationValues) =>
      translate(key, values, language),
    [language],
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      languages: LANGUAGE_OPTIONS,
    }),
    [language, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextData => {
  return useContext(I18nContext);
};
